'use client';
import {
  FC,
  Fragment,
  JSX,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import Modal, { ModalPropTypes } from '@/layouts/Modal';
import { sleep } from '@/utils/helper';
import {
  CheckIcon,
  CloseIcon,
  LocationPinIcon,
  MagnifyingGlassIcon,
} from '@/icons';
import { Button, Svg, Text } from '@/components';
import { useLocation } from '@/context/locationContext';
import {
  getGoogleReverseGeocode,
  getNearbyStores,
  updateAddressCookie,
} from '@/app/actions';
import { RadioGroup, Radio } from '@headlessui/react';
import { AddressUserDetails } from '@/models/user-details';
import { useUser } from '@/context/userContext';
import { twMerge } from 'tailwind-merge';
import AddressCard from '@/components/AddressCard';
import { storage } from '@/utils/storage';
import {
  DEFAULT_STOCK_ID,
  DEFAULT_STORE_CODE,
  GURUGRAM_STORE_CODE,
  TOKEN_EXPIRY_TIME,
} from '@/constants';
import { useCart } from '@/context/cartContext';
import { endpoints, http, logger } from '@/config';
import EditIcon from '../../../../public/images/global/EditIcon.svg';
import Image from 'next/image';
import LocationIcon from '@/icons/LocationIcon';
import { useCleverTap } from '@/context/clevertapContext';
import StorePickupIcon from '@/icons/StorePickupIcon';
import { Zap } from 'lucide-react';
import { setCookie } from 'cookies-next';

type PropTypes = ModalPropTypes & {
  redirectTo?: string;
  asGuest?: boolean;
  message?: string;
  eventMetadata?: {
    source?: string;
  };
};

const DeliveryDetails: FC<PropTypes> = ({
  show,
  onClose,
  eventMetadata,
}): JSX.Element => {
  const addressString = storage.get('addressString');

  const {
    setShowSearchManually,
    setShowPickupStore,
    setPendingAddressChange,
    pendingAddressChange,
    getMagentoStoreData,
    setEmptyCart,
    emptyCart,
    setDeliveryType,
    setSelectedStore,
    setShowStoreChangeModal,
    handleSuggestionClick,
    setIsDelivery,
    setHeaderLocationTitle,
    setAddress,
    setCoordinates,
    setLocationToast,
    setStoreCode,
    setAddressString,
    isProcessingChange,
    geolocationOptions,
    setIsProcessingChange,
    setShowHomeDelivery,
    showHomeDelivery,
    handleStoreChange,
    setIsExpressDelivery,
    defaultLat,
    defaultLng,
  } = useLocation();
  const { userDetails } = useUser();
  const { fetchUpdateCart, cartItems } = useCart();
  const { pushEvent } = useCleverTap();

  const [locationError, setLocationError] = useState<string | null>(null);
  const [stores, setStores] = useState<any[]>([]);
  const currentStoreCode = storage.get('store_code') || '';
  const [locationPermission, setLocationPermission] = useState<
    'granted' | 'denied' | 'prompt' | 'default' | null
  >(null);
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [selectedType, setSelectedType] = useState<'home' | 'pickup'>(
    showHomeDelivery
      ? 'home'
      : (storage.get('deliveryType') as 'home' | 'pickup')
  );
  const [tempSelected, setTempSelected] = useState<AddressUserDetails | null>(
    null
  );

  const fetchStores = useCallback(async () => {
    const data = await getNearbyStores(
      defaultLat ?? undefined,
      defaultLng ?? undefined
    );
    if (data?.store) {
      // Sort stores to show current store first
      const sortedStores = [...data.store].sort((a, b) => {
        if (a.code === currentStoreCode) return -1;
        if (b.code === currentStoreCode) return 1;
        return 0;
      });
      setStores(sortedStores);
    }
  }, [currentStoreCode]);

  const addressesExist = useMemo(() => {
    if (userDetails?.addresses && userDetails?.addresses.length > 0) {
      return true;
    }
    return false;
  }, [userDetails]);

  const defaultShippingBillingAddress: AddressUserDetails | null =
    useMemo(() => {
      if (userDetails?.default_shipping) {
        return userDetails?.addresses?.find(
          (item: AddressUserDetails) =>
            Number(userDetails?.default_shipping) === item.id
        );
      } else {
        return null;
      }
    }, [userDetails?.addresses, userDetails?.default_shipping]);

  const addresses = useMemo(
    () =>
      userDetails?.addresses?.sort(({ id }: AddressUserDetails) =>
        defaultShippingBillingAddress?.id === id ? -1 : 0
      ),
    [userDetails?.addresses, defaultShippingBillingAddress?.id]
  );

  const onDeliveryDetailModalClose = useCallback(async () => {
    onClose();
    await sleep(1000);
    setLocationError(null); // Clear any previous error
  }, [onClose]);

  const handleSelected = useCallback(
    async (e: AddressUserDetails) => {
      setSelectedStore(null);
      setTempSelected(e);
      const latitude =
        e?.custom_attributes?.[0]?.value || storage.get('lat') || '';
      const longitude =
        e?.custom_attributes?.[1]?.value || storage.get('lng') || '';
      setCoordinates({ lat: parseFloat(latitude), lng: parseFloat(longitude) });
      const dataString = await getGoogleReverseGeocode(
        parseFloat(latitude),
        parseFloat(longitude),
        0
      );

      const address =
        e?.street[0] +
        ', ' +
        (e?.street[1] ? e?.street[1] + ', ' : '') +
        ', ' +
        e?.city +
        ', ' +
        e?.region.region;
      if (dataString) {
        const { data, cartKeys } = JSON.parse(dataString);
        if (data?.properties[0]?.express_delivery === 1) {
          setIsExpressDelivery(true);
          storage.set('isExpressDelivery', 'true');
        } else {
          setIsExpressDelivery(false);
          storage.set('isExpressDelivery', 'false');
        }
        const storeCode =
          data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE;
        setIsDelivery(true);
        storage.set('isDelivery', 'true');
        if (data.store_code) {
          if (
            currentStoreCode &&
            currentStoreCode !== data.store_code &&
            cartItems &&
            cartItems.length > 0
          ) {
            setPendingAddressChange({
              lat: parseFloat(latitude),
              lng: parseFloat(longitude),
              prediction: {
                description: address,
              },
              data,
              id: e.id,
            });
            setShowStoreChangeModal(true);
            return;
          } else if (currentStoreCode && currentStoreCode !== data.store_code) {
            await handleStoreChange({
              stock_id: data?.stock_id,
              store_code: storeCode,
              default_store_code: data?.default_store_code,
              wizzy_api_key: data?.wizzy_api_key,
              wizzy_store_id: data?.wizzy_store_id,
            });
          }
        }

        storage.set('selectedAddressId', String(e.id));
        if (data?.store_code === '') {
          storage.set('serviceable', 'false');
          setAddress(address);
          updateAddressCookie(address, address);
        } else {
          storage.set('serviceable', 'true');
          storage.set('lat', latitude);
          storage.set('lng', longitude);
          storage.set('selectedAddressId', e?.id.toString());
          setAddress(address);
          updateAddressCookie(address, address);
        }
        storage.set(
          'default_store_code',
          data?.default_store_code || DEFAULT_STORE_CODE
        );
        // Get title up to first comma or use full text if no comma
        const titleText = e?.street[0]?.split(',')[0] || e?.street[0];
        storage.set('addressTitle', titleText);
        storage.set('address', titleText);
        setHeaderLocationTitle(titleText);
        storage.set('addressDescription', address);
        storage.set('store_code', storeCode);
        storage.set('home_store_code', storeCode);
        setStoreCode(storeCode);
        storage.set('stock_id', data?.stock_id || DEFAULT_STOCK_ID);
        storage.set('wizzy_api_key', data?.wizzy_api_key);
        storage.set('wizzy_store_id', data?.wizzy_store_id);
        storage.set('addressString', address);
        setAddressString(address);
        onDeliveryDetailModalClose();
        if (cartKeys) {
          storage.set(cartKeys?.key, cartKeys?.value);
          fetchUpdateCart();
        }

        if (currentStoreCode !== storeCode) {
          storage.set('show_location_toast', 'true');
          window.location.reload();
        } else {
          setLocationToast({
            message:
              "You're in our service area! Delivery typically takes 3-4 hours",
            deliveryType: 'home',
          });
          setTimeout(() => setLocationToast(undefined), 2000);
        }
      }
    },
    [
      currentStoreCode,
      fetchUpdateCart,
      onDeliveryDetailModalClose,
      setAddress,
      setAddressString,
      setCoordinates,
      setHeaderLocationTitle,
      setPendingAddressChange,
      setSelectedStore,
      setShowStoreChangeModal,
      setStoreCode,
      updateAddressCookie,
    ]
  );

  const handleHomeDelivery = useCallback(() => {
    setSelectedType('home');
    const eventData: any = {
      'Page URL': window.location.href,
      'Page Title': document.title,
      'Selected Method': 'Home Delivery',
      'Store Code': storage?.get('store_code'),
      Serviceable: storage?.get('serviceable'),
    };
    if (eventMetadata?.source) {
      eventData['Source'] = eventMetadata?.source;
    }
    pushEvent({
      event: 'Delivery Details Dialog Clicked',
      data: eventData,
    });
  }, []);

  const handlePickupDelivery = useCallback(() => {
    setSelectedType('pickup');
    const eventData: any = {
      'Page URL': window.location.href,
      'Page Title': document.title,
      'Selected Method': 'Pickup',
      'Store Code': storage?.get('store_code'),
      Serviceable: storage?.get('serviceable'),
    };
    if (eventMetadata?.source) {
      eventData['Source'] = eventMetadata?.source;
    }
    pushEvent({
      event: 'Delivery Details Dialog Clicked',
      data: eventData,
    });
  }, []);

  const handleOpenSearchManually = useCallback(() => {
    storage.set('isDelivery', 'true');
    setIsDelivery(true);
    setShowSearchManually(true);
    onClose();
  }, [onClose, setIsDelivery, setShowSearchManually]);

  const handleOpenPickupStore = useCallback(() => {
    setIsDelivery(false);
    setShowPickupStore(true);
    onClose();
  }, [onClose, setIsDelivery, setShowPickupStore]);

  const processAddressChange = useCallback(
    async (
      prediction: any,
      data: any,
      lat?: number,
      lng?: number,
      id?: number
    ) => {
      try {
        const selectedAddress = prediction.description;

        // Update all address states
        setAddress(selectedAddress);
        storage.set('wizzy_api_key', data?.wizzy_api_key);
        storage.set('wizzy_store_id', data?.wizzy_store_id);
        if (data?.store_code) {
          storage.set('deliveryType', 'home');
          setDeliveryType('home');
        }
        // Store address and related data
        storage.set('addressString', selectedAddress);
        setAddressString(selectedAddress);
        if (data?.result?.[0]?.lat && data?.result?.[0]?.lng) {
          storage.set('lat', data?.result?.[0]?.lat);
          storage.set('lng', data?.result?.[0]?.lng);
        }
        if (lat && lng) {
          storage.set('lat', lat.toString());
          storage.set('lng', lng.toString());
        }
        if (id) {
          storage.set('selectedAddressId', data?.id.toString());
        }

        let cityPart = '';
        let statePart = '';

        if (data?.result?.[0]?.city && data?.result?.[0]?.state) {
          cityPart = data?.result?.[0]?.city;
          statePart = data?.result?.[0]?.state;
        } else {
          if (!cityPart || !statePart) {
            const addressParts = selectedAddress
              .split(',')
              .map((part: string) => part.trim());

            // Try to find city and state in address parts
            cityPart = addressParts[addressParts.length - 3];
            statePart = addressParts[addressParts.length - 2];
          }
        }

        // Store the components
        storage.set('city', cityPart);
        storage.set('state', statePart);
        storage.set(
          'store_code',
          data?.store_code || data?.default_store_code || DEFAULT_STORE_CODE
        );
        storage.set('stock_id', data?.stock_id || DEFAULT_STOCK_ID);
        storage.set('home_store_code', data?.store_code);
      } catch (error) {
        logger.error('Error in processAddressChange:', error);
        throw error;
      }
    },
    [setAddress, setAddressString, setDeliveryType]
  );
  useEffect(() => {
    setShowHomeDelivery(false);
  }, []);

  const handleError = useCallback(
    (error: { code: number; message: any }, reject: (arg0: any) => void) => {
      if (error.code === 1) {
        pushEvent({
          event: 'GPS Location Permission Query',
          data: {
            'Page URL': window.location.href,
            'Page Name': document.title,
            Permission: 'Denied',
            'Prompt Source': 'Delivery Details',
            Error: error?.message,
          },
        });
        setLocationPermission('denied');
      }
      reject(error);
    },
    [setLocationPermission]
  );

  const handleSuccess = useCallback(
    (
      position: { coords: { latitude: any; longitude: any; accuracy: any } },
      resolve: (arg0: any) => void
    ) => {
      pushEvent({
        event: 'GPS Location Permission Query',
        data: {
          'Page URL': window.location.href,
          'Page Name': document.title,
          Permission: 'Granted',
          Position: `${position?.coords?.latitude}, ${position?.coords?.longitude}`,
          Accuracy: position?.coords?.accuracy,
          'Prompt Source': 'Delivery Details',
        },
      });
      setLocationPermission('granted');
      resolve(position);
    },
    [setLocationPermission]
  );

  const handleGetCurrentLocation = useCallback(async () => {
    try {
      setTempSelected(null);
      setSelectedStore(null);
      storage.set('selectedAddressId', '');
      setLocationError(null);
      setIsFetchingLocation(true);
      setLocationError(null);
      setAddressString(''); // Clear the old address immediately
      storage.set('addressString', '');
      if (!navigator.geolocation) {
        setLocationError('Geolocation is not supported by your browser.');
        setIsFetchingLocation(false);
        return;
      }

      if (locationPermission === 'denied') {
        setLocationError(
          'Location access is blocked. Please enable location access in your browser settings and try again.'
        );
        return;
      }

      const position = await new Promise<GeolocationPosition>(
        (resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            position => handleSuccess(position, resolve),
            error => handleError(error, reject),
            geolocationOptions
          );
        }
      );

      const { latitude, longitude } = position.coords;
      const geocodeDataString = await getGoogleReverseGeocode(
        latitude,
        longitude,
        1
      );

      if (geocodeDataString) {
        const { data } = JSON.parse(geocodeDataString);
        const storeCode =
          data.store_code || data.default_store_code || DEFAULT_STORE_CODE;
        if (storeCode) {
          const currentStoreCode = storage.get('store_code');
          pushEvent({
            event: 'GPS Location Fetched',
            data: {
              'Page URL': window.location.href,
              'Page Name': document.title,
              Serviceable: data.store_code !== '' || false,
              'Store Code': storeCode,
              'Prompt Source': 'Non-Serviceable Dialog',
            },
          });

          if (
            currentStoreCode &&
            data.store_code !== currentStoreCode &&
            cartItems &&
            cartItems.length > 0
          ) {
            setPendingAddressChange({
              lat: latitude,
              lng: longitude,
              prediction: {
                place_id: data.result?.[0]?.place_id,
                description:
                  data.result?.[0]?.title + ',' + data.result?.[0]?.sub_title,
              },
              data,
            });
            setShowStoreChangeModal(true);
            return;
          } else if (currentStoreCode && currentStoreCode !== data.store_code) {
            await handleStoreChange({
              stock_id: data?.stock_id,
              store_code: storeCode,
              default_store_code: data?.default_store_code,
              wizzy_api_key: data?.wizzy_api_key,
              wizzy_store_id: data?.wizzy_store_id,
            });
          }
        }

        setIsDelivery(true);
        storage.set('isDelivery', 'true');
        await getMagentoStoreData({
          coords: {
            latitude,
            longitude,
            accuracy: position.coords.accuracy,
            skipToast: true,
            intentionalClick: 1,
            isDeliveryPopup: currentStoreCode !== storeCode ? false : true,
          },
        });

        if (data?.result?.[0]) {
          const addressString =
            data.result[0].title + ',' + data.result[0].sub_title;
          await processAddressChange(
            {
              place_id: data.result[0].place_id,
              description: addressString,
            },
            data,
            latitude,
            longitude
          );
        }
      }
    } catch (error: any) {
      logger.error('Error getting current location:', error);
      let errorMessage = 'Unable to retrieve location.';
      if (error.code === 1) {
        errorMessage =
          'Please allow location access and try again or search location manually.';
      }
      if (error.code === 2) errorMessage = 'Position unavailable.';
      if (error.code === 3) errorMessage = 'Location request timed out.';
      setLocationError(errorMessage);
      logger.error(errorMessage);

      // Reset address on error
      setAddressString('');
      storage.set('addressString', '');
      setHeaderLocationTitle('Select Location');
    } finally {
      setIsFetchingLocation(false);
    }
  }, [
    getMagentoStoreData,
    locationPermission,
    processAddressChange,
    pushEvent,
    setIsDelivery,
    setPendingAddressChange,
    setSelectedStore,
    setShowStoreChangeModal,
  ]);

  const handleAddressChange = useCallback(async () => {
    if (tempSelected) {
      await handleSelected(tempSelected);
    }
    if (storage.get('store_code') !== storage.get('home_store_code')) {
      const prediction = JSON.parse(storage.get('prediction') || '{}');
      if (prediction && storage.get('placeId')) {
        await handleSuggestionClick({
          prediction: prediction,
          address: prediction.description,
          skipToast: false,
        });
        const eventData: any = {
          'Page URL': window.location.href,
          'Page Title': document.title,
          'Selected Method': 'Home Delivery',
          'Selected Store Code': storage.get('store_code'),
          Serviceable: storage.get('serviceable'),
        };
        if (eventMetadata?.source) {
          eventData['Source'] = eventMetadata?.source;
        }
        pushEvent({
          event: 'Delivery Details Dialog Continue',
          data: eventData,
        });
        onClose();
        return;
      } else {
        await getMagentoStoreData({
          coords: {
            latitude: Number(storage.get('lat')),
            longitude: Number(storage.get('lng')),
            skipToast: false,
            intentionalClick: 1,
          },
        });
        const eventData: any = {
          'Page URL': window.location.href,
          'Page Title': document.title,
          'Selected Method': 'Home Delivery',
          'Selected Store Code': storage.get('store_code'),
          Serviceable: storage.get('serviceable'),
        };
        if (eventMetadata?.source) {
          eventData['Source'] = eventMetadata?.source;
        }
        pushEvent({
          event: 'Delivery Details Dialog Continue',
          data: eventData,
        });
        onClose();
        return;
      }
    }
    storage.set('deliveryType', 'home');
    setDeliveryType('home');
    const addressTitle = storage.get('addressTitle');
    if (addressTitle) {
      storage.set('address', addressTitle);
      setHeaderLocationTitle(addressTitle);
    }
    onClose();
    if (!tempSelected) {
      setLocationToast({
        message:
          "You're in our service area! Delivery typically takes 3-4 hours",
        deliveryType: 'home',
      });
      setTimeout(() => setLocationToast(undefined), 2000);
    }
  }, [
    getMagentoStoreData,
    handleSelected,
    handleSuggestionClick,
    onClose,
    setDeliveryType,
    setHeaderLocationTitle,
    setLocationToast,
    tempSelected,
  ]);

  const handleStoreSelect = useCallback(
    async (store: any) => {
      const eventData: any = {
        'Page URL': window.location.href,
        'Page Title': document.title,
        'Selected Method': 'Pickup',
        'Selected Store Code': store?.code,
      };
      if (eventMetadata?.source) {
        eventData['Source'] = eventMetadata?.source;
      }
      pushEvent({
        event: 'Delivery Details Dialog Continue',
        data: eventData,
      });
      storage.set('deliveryType', 'pickup');
      setDeliveryType('pickup');
      storage.set('store_code', store?.code);
      setStoreCode(store?.code);
      storage.set('stock_id', store?.stock_id || DEFAULT_STOCK_ID);
      storage.set('wizzy_api_key', store?.wizzy_api_key);
      storage.set('wizzy_store_id', store?.wizzy_store_id);
      storage.set('address', store.name);
      setHeaderLocationTitle(store.name);
      storage.set('storeTitle', store.name);
      storage.set('storeDescription', `${store.street[0]} ${store.street[1]}`);
      const cookiesToSet = [
        { key: 'store_code', value: store.code },
        { key: 'stock_id', value: store?.stock_id || DEFAULT_STOCK_ID },
        { key: 'wizzy_api_key', value: store?.wizzy_api_key },
        { key: 'wizzy_store_id', value: store?.wizzy_store_id },
      ];
      for (const cookie of cookiesToSet) {
        setCookie(cookie.key, cookie.value, {
          maxAge: TOKEN_EXPIRY_TIME,
        });
      }
      onClose();

      setLocationToast({
        message: 'Your Pickup Store has been changed.',
        deliveryType: 'pickup',
      });
      setTimeout(() => setLocationToast(undefined), 2000);
    },
    [
      onClose,
      setDeliveryType,
      setHeaderLocationTitle,
      setLocationToast,
      setStoreCode,
    ]
  );

  useEffect(() => {
    const isNotServiceable = storage.get('serviceable') === 'false';
    if (isNotServiceable) {
      const addressReset = storage.get('address');
      const addressTitle = storage.get('addressTitle');
      const addressDescription = storage.get('addressDescription');
      if (addressReset && addressTitle && addressReset === addressTitle) {
        storage.set('addressString', addressReset + ',' + addressDescription);
        setAddressString(addressReset + ',' + addressDescription);
        storage.set('serviceable', 'true');
      }
    }
  }, []);

  useEffect(() => {
    fetchStores();
  }, [currentStoreCode, fetchStores]);

  useEffect(() => {
    const cachedAddressId = storage.get('selectedAddressId');
    if (cachedAddressId && addresses) {
      const selectedAddresses =
        addresses.find(
          (addr: { id: number }) => addr.id === Number(cachedAddressId)
        ) || null;
      setTempSelected(selectedAddresses);
    }
  }, [addresses]);

  useEffect(() => {
    if (show) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [show]);

  useEffect(() => {
    const handlePendingChange = async () => {
      if (emptyCart && pendingAddressChange && !isProcessingChange) {
        try {
          setIsProcessingChange(true);

          // Process address change immediately
          await processAddressChange(
            pendingAddressChange.prediction,
            pendingAddressChange.data
          );
          // Reset states
          setShowStoreChangeModal(false);
          setPendingAddressChange(null);
          setIsProcessingChange(false);
          setEmptyCart(false);
        } catch (error) {
          logger.error('Error handling pending change:', error);
          setEmptyCart(false);
          setPendingAddressChange(null);
          setIsProcessingChange(false);
          setShowStoreChangeModal(false);
        }
      }
    };

    handlePendingChange();
  }, [
    emptyCart,
    pendingAddressChange,
    isProcessingChange,
    processAddressChange,
    setEmptyCart,
    setPendingAddressChange,
    setShowStoreChangeModal,
  ]);

  useEffect(() => {
    const eventData: any = {
      'Page URL': window.location.href,
      'Page Title': document.title,
      'Selected Method': selectedType === 'home' ? 'Home Delivery' : 'Pickup',
      'Store Code': storage?.get('store_code'),
      Serviceable: storage?.get('serviceable'),
    };
    if (eventMetadata?.source) {
      eventData['Source'] = eventMetadata?.source;
    }
    pushEvent({
      event: 'Delivery Details Dialog Shown',
      data: eventData,
    });
  }, []);

  const [expressStandard, setExpressStandard] = useState(false);
  useEffect(() => {
    fetchDeliveryMethods();
  }, []);

  const fetchDeliveryMethods = async () => {
    const lat = storage.get('lat');
    const lng = storage.get('lng');
    const url = `${endpoints.EXPRESS_STANDARD_CHECK_SERVICEABILITY}?latitude=${lat}&longitude=${lng}`;
    const {
      response,
      data,
    }: { response: any; data: { express_standard_delivery: boolean } } =
      await http.get(url);
    if (response.ok) {
      setExpressStandard(data.express_standard_delivery);
    }
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      showCloseButton={false}
      id='location-flow'
      heightClassName='max-md:max-h-[90%]'
      style='transition-all duration-300 lg:fixed lg:translate-x-0 rounded-t-3xl rounded-b-none'
      preventScroll={true}>
      <div
        className={twMerge(
          'items-left flex flex-col justify-start gap-y-4 max-lg:w-full h-full'
        )}>
        <div aria-label='modal-header' className='text-center'>
          <Text className='-mt-3 items-center px-3 py-0 text-xl font-semibold'>
            Choose Your Delivery Location
          </Text>
        </div>
        <div className='mt-2 flex w-full flex-row justify-between gap-x-2'>
          <button
            onClick={handleHomeDelivery}
            className={twMerge(
              'flex flex-1 flex-row items-center justify-center rounded-[40px] border border-[#a5a4a7] px-1 py-2 duration-300 in-[.group]:transition-all',
              selectedType === 'home'
                ? 'border-fig bg-fig'
                : 'border-[#a5a4a7] bg-white'
            )}>
            <Svg
              className={twMerge(
                'mt-1 h-5 w-5',
                selectedType === 'home' ? 'stroke-white' : 'stroke-[#38373A]'
              )}>
              <LocationPinIcon />
            </Svg>
            <span
              className={twMerge(
                'text-sm font-semibold',
                selectedType === 'home' ? 'text-white' : 'text-[#38373A]'
              )}>
              Home Delivery
            </span>
          </button>

          <button
            onClick={handlePickupDelivery}
            className={twMerge(
              'flex flex-1 flex-row items-center justify-center gap-[7px] rounded-[40px] border px-2 py-2 transition-all duration-300',
              selectedType === 'pickup'
                ? 'border-fig bg-fig'
                : 'border-[#a5a4a7] bg-white'
            )}>
            <Svg
              fill={selectedType === 'pickup' ? '#ffffff' : '#38373A'}
              className={twMerge(
                'h-5 w-5 stroke-fig stroke-[0.4] mt-1 -mr-1',
                selectedType === 'pickup' ? 'stroke-white' : 'stroke-[#38373A]'
              )}>
              <StorePickupIcon />
            </Svg>
            <div
              className={twMerge(
                'no-wrap text-sm font-semibold max-[320px]:-ml-4',
                selectedType === 'pickup' ? 'text-white' : 'text-[#38373A]'
              )}>
              Pickup From Store
            </div>
          </button>
        </div>

        {/* Content Container */}
        <div
          className={twMerge(
            'relative flex flex-col flex-1',
            selectedType === 'home'
              ? 'overflow-y-auto max-h-[calc(75vh-180px)] pb-20'
              : ''
          )}>
          {selectedType === 'home' ? (
            <div className='mt-3 items-start'>
              <div
                className={twMerge(
                  'rounded-lg border border-[#38373A]',
                  storage.get('serviceable') === 'false' &&
                    'border-[#ecbcbf] bg-pale-rose'
                )}>
                <div className='flex flex-row items-center justify-between py-3 pr-2'>
                  <div
                    className='flex-1 cursor-pointer'
                    onClick={handleOpenSearchManually}>
                    <button className='flex cursor-pointer flex-row items-center justify-center gap-2'>
                      <Text
                        className={twMerge(
                          'cursor-pointer text-base',
                          (isFetchingLocation || isProcessingChange) &&
                            'text-gray-400'
                        )}>
                        {isFetchingLocation || isProcessingChange
                          ? 'Fetching your location...'
                          : addressString &&
                              addressString !== 'undefined,undefined'
                            ? addressString.length >
                              (typeof window !== 'undefined' &&
                              window.innerWidth < 550
                                ? 20
                                : 36)
                              ? addressString.substring(
                                  0,
                                  typeof window !== 'undefined' &&
                                    window.innerWidth < 550
                                    ? 20
                                    : 36
                                ) + '...'
                              : addressString
                            : 'Select Location'}
                      </Text>
                    </button>
                  </div>

                  <div className='flex flex-row items-center justify-center gap-3'>
                    {addressString ? (
                      <button onClick={handleOpenSearchManually}>
                        <Svg className='h-5 w-5 cursor-pointer'>
                          <CloseIcon />
                        </Svg>
                      </button>
                    ) : (
                      <button onClick={handleOpenSearchManually}>
                        <Svg className='h-5 w-5 stroke-[#A0383F]'>
                          <MagnifyingGlassIcon />
                        </Svg>
                      </button>
                    )}
                    <div className='h-4 w-px bg-[#cccccd]' />
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        handleGetCurrentLocation();
                      }}>
                      <Svg className='mr-1 h-3.5 w-3.5 cursor-pointer stroke-fig'>
                        <LocationIcon />
                      </Svg>
                    </button>
                  </div>
                </div>
              </div>
              {locationError && (
                <Text className='mt-2 text-left text-xs text-fig'>
                  {locationError}
                </Text>
              )}
              {!locationError && addressString && !isFetchingLocation && (
                <div className='mt-2 text-left text-sm'>
                  {storage.get('serviceable') === 'true' ? (
                    storage.get('store_code') === GURUGRAM_STORE_CODE ||
                    expressStandard ? (
                      <div className='flex items-center gap-x-1 pl-1'>
                        <Zap
                          size={14}
                          className='shrink-0 stroke-fig text-fig'
                        />
                        <Text className='p-0 text-xs'>
                          <span className='font-semibold text-fig'>
                            EXPRESS DELIVERY
                          </span>
                          <span className='text-[#4A4A4A]'> in </span>
                          <span className='font-semibold text-fig'>
                            2 hours
                          </span>
                          <span className='text-[#4A4A4A]'>
                            , at your delivery location
                          </span>
                        </Text>
                      </div>
                    ) : (
                      <div className='flex items-center gap-x-1 pl-1'>
                        <Svg className='rounded-full bg-fig stroke-white p-1'>
                          <CheckIcon />
                        </Svg>
                        <Text className='p-0 text-xs text-fig'>
                          We can deliver to your location
                        </Text>
                      </div>
                    )
                  ) : (
                    <div className='flex items-center gap-2'>
                      <Text className='p-0 text-xs text-fig'>
                        We're not available in your area yet. Please try a
                        different location or consider pickup from store.
                      </Text>
                    </div>
                  )}
                </div>
              )}
              {addressesExist && selectedType === 'home' && (
                <div className='transition-all duration-300'>
                  <RadioGroup
                    value={tempSelected}
                    onChange={setTempSelected}
                    className='mt-6 w-full space-y-3'>
                    <div className='flex w-full flex-col gap-y-4  lg:px-0'>
                      <Text className='-my-2 px-0 text-left text-sm  font-semibold text-black'>
                        Saved Addresses
                      </Text>
                      {addresses.map((address: AddressUserDetails) => (
                        <Fragment key={address.id}>
                          <Radio
                            value={address}
                            className={({ checked }) =>
                              twMerge(
                                'flex w-full cursor-pointer rounded-lg',
                                checked &&
                                  'base-radio-selected border border-fig'
                              )
                            }>
                            {({ checked }: any) => (
                              <div className='w-full'>
                                <AddressCard
                                  address={address}
                                  layout='Location'
                                  checked={checked}
                                />
                              </div>
                            )}
                          </Radio>
                        </Fragment>
                      ))}
                    </div>
                  </RadioGroup>
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-col max-lg:flex-1'>
              {stores.length > 0 ? (
                <div className='relative mt-3 max-lg:flex-1'>
                  <div className='flex flex-row items-center justify-between rounded-lg border border-[#cccccd] p-3 text-left'>
                    <div className='flex w-full flex-col gap-1 pr-7'>
                      <Text className='px-0 text-sm font-semibold'>
                        {stores[0].name}
                      </Text>
                      <Text className='px-0 text-xs'>
                        {stores[0].street_line1}
                        {stores[0].street_line2 &&
                          `, ${stores[0].street_line2}`}
                        , {stores[0].city} {stores[0].postcode}
                      </Text>
                    </div>
                    <button
                      onClick={handleOpenPickupStore}
                      className='flex flex-row items-center justify-center gap-2'>
                      <Image src={EditIcon} alt='icon' className='' />
                    </button>
                  </div>
                </div>
              ) : (
                <div className='base-loading w-full h-20 rounded-lg' />
              )}
              <Button
                label='CONTINUE'
                className='mt-5 w-full py-3'
                disabled={!stores || stores?.length < 1}
                onClick={() => handleStoreSelect(stores[0])}
              />
            </div>
          )}
        </div>

        {/* Fixed Button Container - Only for Home Delivery */}
        {selectedType === 'home' && (
          <div className='fixed bottom-0 left-0 right-0 bg-white px-5 py-4 border-t border-gray-200'>
            <Button
              label='CONTINUE'
              className={twMerge(
                'w-full py-3',
                !tempSelected &&
                  storage.get('serviceable') !== 'true' &&
                  'cursor-not-allowed bg-[#cccccd] text-[#979699] hover:bg-[#cccccd]'
              )}
              disabled={!tempSelected && storage.get('serviceable') !== 'true'}
              onClick={handleAddressChange}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DeliveryDetails;
